/**
 * CombatLog — Toggleable diagnostic HUD panel for verifying combat in admin
 * mode. Subscribes to client events forwarded by GameClient and renders a
 * scrollable history (last N events kept in a ring buffer; the panel scrolls
 * to reveal older rows). The header shows rolling 5s DPS for the selected
 * local drone plus the rolling 5s crit-rate.
 *
 * Default off; toggle with backtick (handled by GameClient).
 *
 * The class is a thin DOM shell over a pure data layer: the ring-buffer + DPS
 * + crit-rate + classifier helpers are exported below so tests can verify the
 * math without spinning up jsdom. Class-level tests use a small DOM stub.
 */
import { CRIT_MULTIPLIER } from '../../shared/constants';
import { AlgorithmCardType, ServerEvent } from '../../shared/types';

export type CombatLogClassification = 'NORMAL' | 'CRIT' | 'SHIELD' | 'HEAL' | 'KILL' | 'STREAK';

/**
 * Provenance of the damage/heal row. BASIC = vanilla auto-attack or manual
 * basic-fire volley; ABILITY = active drone ability (Striker Charge, Sniper
 * Pierce); CARD = card-driven damage (currently ChoreoUltimate AoE). Empty
 * string means we couldn't classify (only used for non-damage rows like
 * SHIELD / KILL / STREAK / HEAL).
 */
export type CombatLogSource = 'BASIC' | 'ABILITY' | 'CARD' | '';

/**
 * Optional damage-breakdown captured for tooltip. The CombatLog never
 * fabricates components it doesn't have evidence for: only `crit` is
 * derivable from the wire payload (final / 1.5). Aura/buff multipliers
 * are not currently broadcast, so their fields stay undefined.
 */
export interface CombatLogBreakdown {
  base: number;
  crit?: number; // 1.5 when isCrit, undefined otherwise
  final: number;
}

export interface CombatLogEvent {
  /** ms since epoch for this event (recorded at insertion time). */
  timestamp: number;
  classification: CombatLogClassification;
  /** Free-form attacker label (username or drone id fragment). Empty when not known. */
  attackerName: string;
  /** Free-form target label. Empty when not known. */
  targetName: string;
  /** Damage / heal amount; zero for SHIELD / KILL / STREAK rows. */
  amount: number;
  /** Optional descriptor appended to the right (e.g. "x3" for streaks). */
  detail?: string;
  /** Where the damage came from. Empty for non-damage rows. */
  source: CombatLogSource;
  /** Reconstructed breakdown for the hover tooltip; undefined when unknown. */
  breakdown?: CombatLogBreakdown;
}

interface DpsSample {
  ts: number;
  damage: number;
}

/**
 * One per damage tick on the locally-selected drone. Used to compute the
 * rolling crit-rate% header field.
 */
interface CritSample {
  ts: number;
  isCrit: boolean;
}

/** Default visible row count — kept for back-compat; the actual cap is
 *  {@link MAX_LOG_HISTORY}. The CSS `max-height` on the panel naturally
 *  limits how many of the rendered rows fit on screen at once. */
export const MAX_LOG_ROWS = 12;
/** Maximum rows kept in the scrollable ring buffer. Older rows drop off. */
export const MAX_LOG_HISTORY = 100;
/** Rolling DPS window (ms). */
export const DPS_WINDOW_MS = 5000;
/** Rolling crit-rate window (ms). Same as DPS so they share the lens. */
export const CRIT_WINDOW_MS = 5000;
/**
 * After an AbilityTriggered event, any DamageDealt from the same drone
 * within this window is classified as ABILITY. 200ms covers the same-tick
 * apply (20Hz tick = 50ms) plus jitter without bleeding into later
 * auto-attacks (next attack ≥ 600ms away even for the fastest drone).
 */
export const ABILITY_CORRELATION_WINDOW_MS = 200;
/**
 * After a damage-dealing CardActivated event by some player, any fractional
 * DamageDealt within this window is classified as CARD (currently only
 * ChoreoUltimate — its per-tick AoE damage is < 1).
 */
export const CARD_CORRELATION_WINDOW_MS = 6000;

/* ── Pure helpers (extracted for unit tests) ───────────────────────────── */

/** Format a millisecond timestamp as mm:ss using its local clock value. */
export function formatTimestamp(ms: number): string {
  const date = new Date(ms);
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${mm}:${ss}`;
}

/** Append `event` to `log`, keeping at most `maxRows` entries (oldest dropped). */
export function pushAndCap(
  log: CombatLogEvent[],
  event: CombatLogEvent,
  maxRows: number = MAX_LOG_HISTORY,
): CombatLogEvent[] {
  log.push(event);
  while (log.length > maxRows) log.shift();
  return log;
}

/**
 * Compute rolling DPS from a list of damage samples.
 * Drops samples older than `now - DPS_WINDOW_MS`, then returns
 * `sum(damage) / (DPS_WINDOW_MS / 1000)`.
 *
 * Mutates `samples` in place to evict expired entries (keeps the buffer
 * bounded without an extra allocation per tick).
 */
export function pruneAndComputeDps(samples: DpsSample[], now: number): number {
  const cutoff = now - DPS_WINDOW_MS;
  while (samples.length > 0 && samples[0].ts < cutoff) samples.shift();
  let total = 0;
  for (const s of samples) total += s.damage;
  return total / (DPS_WINDOW_MS / 1000);
}

/**
 * Compute rolling crit-rate (0..100, percent) from a list of crit samples.
 * Returns 0 when the buffer is empty after pruning. Mutates `samples` to
 * drop expired entries (mirrors `pruneAndComputeDps`).
 */
export function pruneAndComputeCritPct(samples: CritSample[], now: number): number {
  const cutoff = now - CRIT_WINDOW_MS;
  while (samples.length > 0 && samples[0].ts < cutoff) samples.shift();
  if (samples.length === 0) return 0;
  let crits = 0;
  for (const s of samples) if (s.isCrit) crits++;
  return (crits / samples.length) * 100;
}

/**
 * Pick a row classification from a server event payload. Falls back to
 * NORMAL when the payload doesn't disambiguate.
 */
export function classifyEvent(eventType: string, data: Record<string, unknown>): CombatLogClassification {
  if (eventType === ServerEvent.DroneDestroyed) return 'KILL';
  if (eventType === ServerEvent.HealApplied) return 'HEAL';
  if (eventType === ServerEvent.ShieldBroken) return 'SHIELD';
  if (eventType === ServerEvent.KillStreak) return 'STREAK';
  if (eventType === ServerEvent.DamageDealt) {
    return data?.isCrit === true ? 'CRIT' : 'NORMAL';
  }
  return 'NORMAL';
}

/**
 * Classify the source of a DamageDealt event using two correlation maps
 * the CombatLog maintains:
 *
 * - `recentAbilityDrones`: droneId → expiry ts (set by AbilityTriggered)
 * - `recentDamageCards`: cardType → expiry ts (set by CardActivated for cards
 *   that deal damage via a non-projectile path, currently ChoreoUltimate)
 *
 * Heuristic order:
 *  1. Attacker drone fired an ability inside the window → ABILITY.
 *  2. A damage-dealing card is active AND the damage value is fractional
 *     (Choreo per-tick is 8 dmg/sec * 0.05 = 0.4) → CARD.
 *  3. Otherwise → BASIC.
 *
 * Non-DamageDealt events return ''.
 */
export function classifySource(
  eventType: string,
  data: Record<string, unknown>,
  recentAbilityDrones: Map<string, number>,
  recentDamageCards: Map<number, number>,
  now: number,
): CombatLogSource {
  if (eventType !== ServerEvent.DamageDealt) return '';

  const attackerDroneId = typeof data.attackerDroneId === 'string' ? data.attackerDroneId : '';
  const abilityExpiry = recentAbilityDrones.get(attackerDroneId);
  if (abilityExpiry !== undefined && abilityExpiry >= now) return 'ABILITY';

  const damage = typeof data.damage === 'number' ? data.damage : 0;
  // ChoreoUltimate per-tick damage is fractional. If any damage card is
  // active in the window AND damage rounded would lose precision, it's
  // almost certainly a Choreo tick.
  if (damage > 0 && damage < 1) {
    for (const expiry of recentDamageCards.values()) {
      if (expiry >= now) return 'CARD';
    }
  }
  return 'BASIC';
}

/**
 * Build a breakdown object for the hover tooltip when we have evidence.
 * Currently only crit hits are reconstructable from the wire payload:
 * `final = floor(base * 1.5)`. For non-crit damage we still return a
 * one-component breakdown so the tooltip can show "X base = X final".
 *
 * Heals and non-damage rows return undefined.
 */
export function buildBreakdown(
  eventType: string,
  data: Record<string, unknown>,
): CombatLogBreakdown | undefined {
  if (eventType !== ServerEvent.DamageDealt) return undefined;
  const damage = typeof data.damage === 'number' ? data.damage : 0;
  if (damage <= 0) return undefined;
  const isCrit = data.isCrit === true;
  if (isCrit) {
    // applyIntegerCritRoll: final = floor(base * 1.5). Reverse: base ≈ final / 1.5.
    const base = Math.round(damage / CRIT_MULTIPLIER);
    return { base, crit: CRIT_MULTIPLIER, final: damage };
  }
  return { base: damage, final: damage };
}

/** Render a breakdown into a single-line tooltip string. */
export function formatBreakdown(breakdown: CombatLogBreakdown | undefined): string {
  if (!breakdown) return '(no breakdown available)';
  const finalStr = formatNumber(breakdown.final);
  if (breakdown.crit !== undefined) {
    return `${formatNumber(breakdown.base)} base × ${breakdown.crit} crit = ${finalStr}`;
  }
  return `${formatNumber(breakdown.base)} base = ${finalStr}`;
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return String(n);
  // One decimal is enough for fractional Choreo ticks.
  return n.toFixed(1);
}

/* ── DOM panel ─────────────────────────────────────────────────────────── */

export interface CombatLogDeps {
  /** Returns the local player's currently-selected drone id (or empty string). */
  getSelectedDroneId: () => string;
  /** Returns the current clock in ms. Pass `Date.now` in production. Tests inject. */
  now: () => number;
  /** Resolve a player session id to a display name (returns empty/null when unknown). */
  resolvePlayerName?: (playerId: string) => string | null;
}

/** Card types that produce DamageDealt via a non-projectile path. */
const DAMAGE_CARDS: ReadonlySet<number> = new Set([AlgorithmCardType.ChoreoUltimate]);

export class CombatLog {
  private parent: HTMLElement;
  private container: HTMLElement;
  private headerEl: HTMLElement;
  private listEl: HTMLElement;
  private deps: CombatLogDeps;
  private events: CombatLogEvent[] = [];
  private dpsSamples: DpsSample[] = [];
  private critSamples: CritSample[] = [];
  /** droneId → expiry-ts (ms). Drones present here are mid-ability. */
  private recentAbilityDrones: Map<string, number> = new Map();
  /** cardType → expiry-ts (ms). Cards present here are recently activated. */
  private recentDamageCards: Map<number, number> = new Map();
  private visible = false;
  private lastRenderedDps = -1;
  private lastRenderedCritPct = -1;

  constructor(parent: HTMLElement, deps: CombatLogDeps) {
    this.parent = parent;
    this.deps = deps;

    this.container = document.createElement('div');
    this.container.className = 'combat-log hidden';

    this.headerEl = document.createElement('div');
    this.headerEl.className = 'combat-log-header';
    this.container.appendChild(this.headerEl);

    this.listEl = document.createElement('div');
    this.listEl.className = 'combat-log-list';
    this.container.appendChild(this.listEl);

    this.parent.appendChild(this.container);
    this.renderHeader();
  }

  /** Toggle the panel visibility. Returns the new visibility state. */
  toggle(): boolean {
    this.visible = !this.visible;
    this.container.classList.toggle('hidden', !this.visible);
    if (this.visible) {
      this.renderRows();
      this.renderHeader();
    }
    return this.visible;
  }

  isVisible(): boolean {
    return this.visible;
  }

  /** Trim expired DPS samples so the header DPS readout doesn't drift when no
   * events arrive for a while. Only re-renders when the displayed value
   * actually changes. */
  update(_deltaMs: number): void {
    if (!this.visible) return;
    this.pruneCorrelationMaps();
    this.renderHeader();
  }

  /** Forward a server-event payload from GameClient. */
  handleEvent(eventType: string, data: Record<string, unknown>): void {
    // Always keep the correlation maps fresh so source classification works
    // regardless of whether the panel is visible.
    this.recordCorrelation(eventType, data);

    if (!this.shouldHandle(eventType)) return;

    const classification = classifyEvent(eventType, data);
    const amount =
      eventType === ServerEvent.DamageDealt
        ? this.numberOr(data.damage, 0)
        : eventType === ServerEvent.HealApplied
          ? this.numberOr(data.amount, 0)
          : 0;

    const source = classifySource(
      eventType,
      data,
      this.recentAbilityDrones,
      this.recentDamageCards,
      this.deps.now(),
    );

    this.recordSelectedDroneSample(eventType, data, amount);

    pushAndCap(this.events, {
      timestamp: this.deps.now(),
      classification,
      attackerName: this.resolveAttackerName(eventType, data),
      targetName: this.resolveTargetName(eventType, data),
      amount: Math.round(amount),
      detail: this.resolveDetail(eventType, data),
      source,
      breakdown: buildBreakdown(eventType, data),
    });
    if (this.visible) {
      this.renderRows();
      this.renderHeader();
    }
  }

  /** Test/diagnostic hook — exposes the captured event ring buffer. */
  getEvents(): ReadonlyArray<CombatLogEvent> {
    return this.events;
  }

  /** Test/diagnostic hook — count of rendered list items. */
  getRowCount(): number {
    return this.listEl.children.length;
  }

  /** Test/diagnostic hook — current rolling DPS for the selected drone. */
  getDps(): number {
    return pruneAndComputeDps(this.dpsSamples, this.deps.now());
  }

  /** Test/diagnostic hook — current rolling crit-rate% for the selected drone. */
  getCritPct(): number {
    return pruneAndComputeCritPct(this.critSamples, this.deps.now());
  }

  /** Test/diagnostic hook — tooltip text rendered on the row at `index`. */
  getRowTooltip(index: number): string {
    const row = this.listEl.children[index] as HTMLElement | undefined;
    return row ? row.title : '';
  }

  dispose(): void {
    this.container.remove();
    this.events = [];
    this.dpsSamples = [];
    this.critSamples = [];
    this.recentAbilityDrones.clear();
    this.recentDamageCards.clear();
    this.visible = false;
  }

  /* ── private ────────────────────────────────────────────────────────── */

  private shouldHandle(eventType: string): boolean {
    return (
      eventType === ServerEvent.DamageDealt ||
      eventType === ServerEvent.DroneDestroyed ||
      eventType === ServerEvent.HealApplied ||
      eventType === ServerEvent.ShieldBroken ||
      eventType === ServerEvent.KillStreak
    );
  }

  /**
   * Update `recentAbilityDrones` / `recentDamageCards` from non-damage
   * server events. Called on every forwarded event (even ones we ignore
   * for the row list) so source classification can correlate later
   * DamageDealt events with the trigger that caused them.
   */
  private recordCorrelation(eventType: string, data: Record<string, unknown>): void {
    const now = this.deps.now();
    if (eventType === ServerEvent.AbilityTriggered) {
      const droneId = this.stringOr(data.droneId, '');
      if (droneId) {
        this.recentAbilityDrones.set(droneId, now + ABILITY_CORRELATION_WINDOW_MS);
      }
    } else if (eventType === ServerEvent.CardActivated) {
      const cardType = this.numberOr(data.cardType, NaN);
      if (Number.isFinite(cardType) && DAMAGE_CARDS.has(cardType)) {
        this.recentDamageCards.set(cardType, now + CARD_CORRELATION_WINDOW_MS);
      }
    }
  }

  /** Drop expired correlation entries so `classifySource` can stop scanning. */
  private pruneCorrelationMaps(): void {
    const now = this.deps.now();
    for (const [k, v] of this.recentAbilityDrones) {
      if (v < now) this.recentAbilityDrones.delete(k);
    }
    for (const [k, v] of this.recentDamageCards) {
      if (v < now) this.recentDamageCards.delete(k);
    }
  }

  /**
   * Record a damage tick into the DPS + crit-rate buffers when the event
   * came from the locally-selected drone. Heals / shields / kills are
   * filtered out by the eventType / amount guards. Both buffers are pruned
   * inline so they stay bounded even while the panel is hidden.
   */
  private recordSelectedDroneSample(
    eventType: string,
    data: Record<string, unknown>,
    amount: number,
  ): void {
    if (eventType !== ServerEvent.DamageDealt) return;
    if (amount <= 0) return;
    const selected = this.deps.getSelectedDroneId();
    if (!selected || data.attackerDroneId !== selected) return;
    const now = this.deps.now();
    this.dpsSamples.push({ ts: now, damage: amount });
    this.critSamples.push({ ts: now, isCrit: data.isCrit === true });
    pruneAndComputeDps(this.dpsSamples, now);
    pruneAndComputeCritPct(this.critSamples, now);
  }

  private resolveAttackerName(eventType: string, data: Record<string, unknown>): string {
    if (eventType === ServerEvent.HealApplied) {
      return this.shortenId(this.stringOr(data.healerDroneId, ''));
    }
    if (eventType === ServerEvent.KillStreak) {
      return this.lookupName(this.stringOr(data.playerId, ''));
    }
    if (eventType === ServerEvent.DroneDestroyed) {
      return this.shortenId(this.stringOr(data.killerId, ''));
    }
    if (eventType === ServerEvent.DamageDealt) {
      return this.shortenId(this.stringOr(data.attackerDroneId, ''));
    }
    return '';
  }

  private resolveTargetName(eventType: string, data: Record<string, unknown>): string {
    if (eventType === ServerEvent.KillStreak) return '';
    if (eventType === ServerEvent.ShieldBroken) {
      return this.shortenId(this.stringOr(data.droneId, ''));
    }
    if (eventType === ServerEvent.DroneDestroyed) {
      return this.shortenId(this.stringOr(data.droneId, ''));
    }
    return this.shortenId(this.stringOr(data.targetDroneId, ''));
  }

  private resolveDetail(eventType: string, data: Record<string, unknown>): string | undefined {
    if (eventType === ServerEvent.KillStreak) {
      const level = this.stringOr(data.level, '');
      const count = this.numberOr(data.killCount, 0);
      return level ? `${level}${count > 0 ? ` x${count}` : ''}` : undefined;
    }
    return undefined;
  }

  private lookupName(playerId: string): string {
    if (!playerId) return '';
    const resolved = this.deps.resolvePlayerName?.(playerId);
    return resolved && resolved.length > 0 ? resolved : this.shortenId(playerId);
  }

  /** Drone / player ids are long uuids; show a short suffix to keep rows tidy. */
  private shortenId(id: string): string {
    if (!id) return '';
    return id.length <= 6 ? id : `…${id.slice(-4)}`;
  }

  private stringOr(value: unknown, fallback: string): string {
    return typeof value === 'string' ? value : fallback;
  }

  private numberOr(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  }

  private renderHeader(): void {
    const now = this.deps.now();
    const dps = pruneAndComputeDps(this.dpsSamples, now);
    const critPct = pruneAndComputeCritPct(this.critSamples, now);
    // Compare to one-decimal precision for DPS, integer percent for crit so
    // 60Hz update ticks don't churn the DOM with identical strings.
    const roundedDps = Math.round(dps * 10) / 10;
    const roundedCrit = Math.round(critPct);
    if (roundedDps === this.lastRenderedDps && roundedCrit === this.lastRenderedCritPct) return;
    this.lastRenderedDps = roundedDps;
    this.lastRenderedCritPct = roundedCrit;
    this.headerEl.innerHTML =
      `<span class="combat-log-title">COMBAT LOG</span>` +
      `<span class="combat-log-stats">` +
      `<span class="combat-log-dps">DPS: ${roundedDps.toFixed(1)}</span>` +
      `<span class="combat-log-crit">Crit: ${roundedCrit}%</span>` +
      `</span>`;
  }

  private renderRows(): void {
    // Capture "was the user at the bottom?" BEFORE innerHTML='' wipes the
    // layout — we only auto-scroll then, leaving manual scroll-ups alone.
    const wasAtBottom =
      this.listEl.scrollTop + this.listEl.clientHeight >= this.listEl.scrollHeight - 4;

    this.listEl.innerHTML = '';
    for (const ev of this.events) {
      const row = document.createElement('div');
      row.className = `combat-log-row badge-${ev.classification.toLowerCase()}`;

      // Native browser tooltip on hover; the test stub stores it in the same
      // `title` field so `getRowTooltip` works in both environments.
      row.title = formatBreakdown(ev.breakdown);

      const ts = document.createElement('span');
      ts.className = 'combat-log-ts';
      ts.textContent = formatTimestamp(ev.timestamp);
      row.appendChild(ts);

      const badge = document.createElement('span');
      badge.className = `combat-log-badge`;
      badge.textContent = ev.classification;
      row.appendChild(badge);

      const source = document.createElement('span');
      source.className = `combat-log-source source-${(ev.source || 'none').toLowerCase()}`;
      source.textContent = ev.source;
      row.appendChild(source);

      const text = document.createElement('span');
      text.className = 'combat-log-text';
      text.textContent = this.composeRowText(ev);
      row.appendChild(text);

      this.listEl.appendChild(row);
    }

    if (wasAtBottom) {
      this.listEl.scrollTop = this.listEl.scrollHeight;
    }
  }

  private composeRowText(ev: CombatLogEvent): string {
    switch (ev.classification) {
      case 'KILL':
        return ev.attackerName
          ? `${ev.attackerName} → ${ev.targetName || '?'}`
          : `destroyed ${ev.targetName || '?'}`;
      case 'STREAK':
        return ev.detail
          ? `${ev.attackerName || '?'} ${ev.detail}`
          : `${ev.attackerName || '?'} streak`;
      case 'SHIELD':
        return `shield broken ${ev.targetName || ''}`.trim();
      case 'HEAL':
        return `${ev.attackerName || '?'} +${ev.amount} → ${ev.targetName || '?'}`;
      default:
        return `${ev.attackerName || '?'} -${ev.amount} → ${ev.targetName || '?'}`;
    }
  }
}
